import type {
  AggregateItem,
  ApiEnvelope,
  FavoriteItem,
  GuestState,
  HistoryItem,
  Hotlist,
  Platform,
  SessionData,
  SourceHealth,
} from "../types";

const rawBase = import.meta.env.VITE_API_BASE_URL || "/api";
const API_BASE = rawBase.replace(/\/$/, "");

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
    throw new ApiError(body?.error || body?.message || "请求失败，请稍后重试。", response.status);
  }

  return (await response.json()) as T;
}

export const api = {
  async getPlatforms(): Promise<Platform[]> {
    return (await request<ApiEnvelope<Platform[]>>("/v1/platforms")).data;
  },
  async getHotlists(): Promise<Hotlist[]> {
    return (await request<ApiEnvelope<Hotlist[]>>("/v1/hotlists")).data;
  },
  async getAggregate(): Promise<AggregateItem[]> {
    return (await request<ApiEnvelope<AggregateItem[]>>("/v1/aggregate")).data;
  },
  async getSession(): Promise<SessionData | null> {
    const response = await request<{ user?: SessionData["user"]; session?: unknown } | null>("/auth/get-session");
    if (!response?.user) return null;
    const admin = await request<{ isAdmin: boolean }>("/v1/me").catch(() => ({ isAdmin: false }));
    return { user: response.user, isAdmin: admin.isAdmin };
  },
  signUp(input: { name: string; email: string; password: string; turnstileToken?: string }) {
    const { turnstileToken, ...body } = input;
    return request<{ user: SessionData["user"] }>("/auth/sign-up/email", {
      method: "POST",
      headers: turnstileToken ? { "x-turnstile-token": turnstileToken } : undefined,
      body: JSON.stringify({ ...body, callbackURL: window.location.origin }),
    });
  },
  signIn(input: { email: string; password: string; turnstileToken?: string }) {
    const { turnstileToken, ...body } = input;
    return request<{ user: SessionData["user"] }>("/auth/sign-in/email", {
      method: "POST",
      headers: turnstileToken ? { "x-turnstile-token": turnstileToken } : undefined,
      body: JSON.stringify({ ...body, rememberMe: true }),
    });
  },
  signOut() {
    return request("/auth/sign-out", { method: "POST", body: "{}" });
  },
  requestPasswordReset(email: string, turnstileToken?: string) {
    return request("/auth/request-password-reset", {
      method: "POST",
      headers: turnstileToken ? { "x-turnstile-token": turnstileToken } : undefined,
      body: JSON.stringify({ email, redirectTo: `${window.location.origin}/reset-password` }),
    });
  },
  async syncGuestState(state: GuestState): Promise<GuestState> {
    return (await request<ApiEnvelope<GuestState>>("/v1/sync", { method: "POST", body: JSON.stringify(state) })).data;
  },
  saveFavorite(item: FavoriteItem) {
    return request("/v1/favorites", { method: "POST", body: JSON.stringify(item) });
  },
  deleteFavorite(key: string) {
    return request(`/v1/favorites/${encodeURIComponent(key)}`, { method: "DELETE" });
  },
  saveHistory(item: HistoryItem) {
    return request("/v1/history", { method: "POST", body: JSON.stringify(item) });
  },
  clearHistory() {
    return request("/v1/history", { method: "DELETE" });
  },
  deleteAccount() {
    return request("/v1/account", { method: "DELETE" });
  },
  saveSubscriptions(state: Pick<GuestState, "subscriptions" | "platformOrder">) {
    return request("/v1/subscriptions", { method: "PUT", body: JSON.stringify(state) });
  },
  async getSourceHealth(): Promise<SourceHealth[]> {
    return (await request<ApiEnvelope<SourceHealth[]>>("/admin/sources")).data;
  },
  refreshSource(platformId: string) {
    return request(`/admin/refresh/${encodeURIComponent(platformId)}`, { method: "POST", body: "{}" });
  },
};

export { ApiError };
